import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useUpdateUserMotivationMutation } from '@/store/api/Users/Users.query'
import { trackOnboardingStepViewed, OnboardingSteps } from '@/utils/analytics'
import type { motivationSchemaType } from '@/utils/schemas'
import { motivationSchema } from '@/utils/schemas'

const useCasesKey = [
  'jobHunting',
  'onlineSafety',
  'visaDocs',
  'personalBrand',
  'datingSafety',
  'vendorCheck',
  'other',
] as const
export const Motivation = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasTrackedView = useRef(false)

  const [selectedUseCase, setSelectedUseCase] = useState<string>('')

  const [updateUserMotivation, { isLoading }] =
    useUpdateUserMotivationMutation()

  // Track step viewed once on mount
  useEffect(() => {
    if (!hasTrackedView.current) {
      trackOnboardingStepViewed(OnboardingSteps.MOTIVATION)
      hasTrackedView.current = true
    }
  }, [])

  const handleUpdateUserMotivation = async (data: motivationSchemaType) => {
    if (selectedUseCase === 'other' && !data.motivation) {
      toast.error(t('signUp.motivation.errorEmpty'))
      return
    }
    try {
      await updateUserMotivation({
        motivation:
          selectedUseCase === 'other'
            ? data.motivation || ''
            : selectedUseCase ?? '',
      }).unwrap()

      navigate(AppRoutes.personalInfo)
    } catch (error) {
      catchWithSentry(error)
    }
  }

  const form = useForm<motivationSchemaType>({
    resolver: zodResolver(motivationSchema),
    defaultValues: {
      motivation: '',
    },
  })

  const { handleSubmit, control } = form

  return (
    <WeScreen useHeader={false}>
      <div className="flex w-full h-full justify-center items-center">
        <div className="flex flex-col gap-8 w-2/3 items-center max-md:w-full">
          <div className="flex flex-col gap-3 items-center">
            <p className="text-[40px] max-md:text-3xl text-neutralColor font-medium text-center">
              {t('signUp.motivation.title')}
            </p>
            <p className="text-2xl max-md:text-xl text-neutralColor80 font-medium text-center">
              {t('signUp.motivation.subtitle')}
            </p>
          </div>
          <div className="flex gap-5 items-center flex-wrap justify-center">
            {useCasesKey.map((key) => (
              <div
                key={key}
                onClick={() => setSelectedUseCase(key)}
                className="py-3 px-6 max-md:px-4 max-md:py-2 bg-baseColor border border-neutralColor30 rounded-3xl cursor-pointer flex items-center gap-2"
              >
                {selectedUseCase === key && (
                  <div className="bg-dynamicGreen rounded-3xl h-2 w-2" />
                )}
                <p className="text-base text-neutralColor font-medium">
                  {t(`signUp.motivation.options.${key}`)}
                </p>
              </div>
            ))}
          </div>
          <FormProvider {...form}>
            <form className="w-full flex gap-8 items-center justify-center max-md:flex-col max-md:mb-6">
              {selectedUseCase === 'other' && (
                <FormField
                  control={control}
                  name="motivation"
                  render={({ field }) => (
                    <FormItem className="w-1/2 max-md:w-full">
                      <FormControl>
                        <Input
                          className="rounded-3xl max-sm:h-12"
                          {...field}
                          placeholder={t('signUp.motivation.placeholder')}
                        />
                      </FormControl>
                      <FormMessage data-name="motivation" />
                    </FormItem>
                  )}
                />
              )}
              <Button
                disabled={selectedUseCase === '' || isLoading}
                className="w-min text-base font-bold max-md:w-full"
                onClick={handleSubmit(handleUpdateUserMotivation)}
              >
                {t('common.continue')}
              </Button>
            </form>
          </FormProvider>
        </div>
      </div>
    </WeScreen>
  )
}
